import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useDesignerStore } from "@/components/designer/store";
import { designerEngine } from "@/components/designer/engine/DesignerEngine";
import { PRODUCTS } from "@/components/designer/products";
import { saveCurrentDesign } from "./saveDesign";
import { pricingApi } from "@/lib/api";
import { DEFAULT_PRICING, estimateUnit, type Pricing } from "@/lib/pricing";

export default function StudioFooter() {
  const { user } = useAuth();
  const product = useDesignerStore((s) => s.product);
  const printMode = useDesignerStore((s) => s.printMode);
  const setPrintMode = useDesignerStore((s) => s.setPrintMode);
  const navigate = useNavigate();

  const [pricing, setPricing] = useState<Pricing>(DEFAULT_PRICING);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    pricingApi.get().then((r) => setPricing(r.pricing)).catch(() => {});
  }, []);
  // Recompute the live estimate as the user edits.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 800);
    return () => clearInterval(t);
  }, []);

  const kind = PRODUCTS[product].kind;
  const size = useMemo(() => designerEngine.getPrintSizeCm(), [tick, product]);
  const { unit, tier } = estimateUnit(pricing, { kind, printMode, wCm: size.width, hCm: size.height });
  const money = (n: number) => pricing.currency + n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  async function requestQuote() {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!designerEngine.getCanvas()) return;
    if (kind === "tshirt" && size.width === 0) {
      setError("Add your artwork to the shirt first.");
      return;
    }
    const name = window.prompt("Name this design for your order:", `${PRODUCTS[product].name} design`);
    if (!name) return;

    setBusy(true);
    setError("");
    try {
      const id = await saveCurrentDesign(product, name);
      const params = new URLSearchParams({
        design: id,
        price: String(unit),
        mode: printMode,
        size: tier ? tier.label : kind === "gangsheet" ? "Gang Sheet (28×100cm)" : "",
      });
      navigate(`/orders/new?${params.toString()}`);
    } catch (e: any) {
      setError(e.message || "Could not save your design");
      setBusy(false);
    }
  }

  return (
    <div className="mt-6 rounded-2xl bg-[#141d2e] border border-slate-700 p-5 flex items-center justify-between flex-wrap gap-6">
      <div className="flex items-center gap-6 flex-wrap">
        {kind === "tshirt" && (
          <div>
            <p className="text-slate-400 text-xs mb-1">Print option</p>
            <div className="flex rounded-lg overflow-hidden border border-slate-600">
              {(
                [
                  ["print_on_garment", "Print onto shirt"],
                  ["print_only", "Supply print only"],
                ] as const
              ).map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => setPrintMode(m)}
                  className={`px-4 py-2 text-sm font-semibold transition ${
                    printMode === m ? "bg-cyan-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-slate-400 text-xs">Print size</p>
          <p className="text-white font-semibold">
            {size.width > 0 ? `${size.width} × ${size.height} cm` : "—"}
            {tier ? ` · ${tier.label.split(" —")[0]}` : ""}
          </p>
        </div>

        <div>
          <p className="text-slate-400 text-xs">Estimated price</p>
          <p className="text-2xl font-black text-white">{money(unit)}</p>
          <p className="text-slate-500 text-[11px]">excl. delivery · we send the final quote</p>
        </div>

        {error && <span className="text-rose-400 text-sm self-center">{error}</span>}
      </div>

      <button
        onClick={requestQuote}
        disabled={busy}
        className="rounded-xl bg-cyan-600 px-8 py-3 text-white font-bold hover:bg-cyan-500 disabled:opacity-60"
      >
        {busy ? "Saving…" : user ? "Save & Request Quote →" : "Sign in to order"}
      </button>
    </div>
  );
}
